import React from "react";
import { Link } from "react-router-dom";
import { Flame, Music2, ShoppingBag, ArrowRight, Heart } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * "The Message" — Robert's artist manifesto page.
 * A single, focused statement of what EVERYTHANG REDNECK stands for.
 * Anchored around the Redneck Revolution album and merch.
 */
export default function Message() {
  return (
    <div className="min-h-screen bg-[#0A0E0B]" data-testid="message-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-[#2C332A]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#DD5422]/15 via-[#0A0E0B] to-[#0A0E0B]" />
        <div className="absolute -right-24 -bottom-24 opacity-10">
          <Flame className="w-[500px] h-[500px] text-[#DD5422]" strokeWidth={0.5} fill="#DD5422" fillOpacity={0.15} />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-4 flex items-center gap-2">
            <Flame className="w-4 h-4" fill="#DD5422" /> / The Message
          </div>
          <h1 className="font-anton text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-[0.9]">
            This ain&apos;t <br />
            just a brand.
            <br />
            <span className="text-[#DD5422]">It&apos;s a stand.</span>
          </h1>
          <p className="text-[#8D9688] mt-8 text-lg max-w-2xl leading-relaxed">
            EVERYTHANG REDNECK started with a beer, a bonfire, and a feeling this country was losing something.
            It became songs. It became shirts. It became a message.
          </p>
        </div>
      </section>

      {/* Manifesto */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-24 space-y-14 text-[#E5E5E5]">
        <Block eyebrow="WHO WE ARE">
          We&apos;re the ones who count our wealth in <strong className="text-[#DD5422]">sunsets, dirt roads, and cold ones.</strong>
          We work with our hands. We ride with our brothers. We stand behind our neighbors. We know the difference between
          <em> loud</em> and <em>strong</em>, and we ain&apos;t apologizin&apos; for either one.
        </Block>

        <Block eyebrow="WHY THE MUSIC">
          Because words on a shirt only go so far. When I sing <em>&ldquo;Rednecks, bikers, brothers, we&apos;ll never hide&rdquo;</em> —
          that&apos;s the thing I&apos;ve been trying to say my whole life. Every track on this record was written for one reason:
          <strong className="text-[#DD5422]"> to remind the people who&apos;ve been forgotten that they&apos;re not alone.</strong>
        </Block>

        <Block eyebrow="WHAT WE STAND FOR">
          <ul className="space-y-3 mt-4">
            <li className="flex items-start gap-3">
              <span className="text-[#DD5422] font-anton shrink-0">01 —</span>
              <span><strong>Loyalty over followers.</strong> Real ones over ten thousand strangers.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#DD5422] font-anton shrink-0">02 —</span>
              <span><strong>Roots over shortcuts.</strong> Small towns raised us. We ain&apos;t forgettin&apos;.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#DD5422] font-anton shrink-0">03 —</span>
              <span><strong>Family, freedom, faith.</strong> In whatever order matters to you.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#DD5422] font-anton shrink-0">04 —</span>
              <span><strong>Unity over division.</strong> Bikers, farmers, veterans, working folks — we ride together.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#DD5422] font-anton shrink-0">05 —</span>
              <span><strong>Taking back what&apos;s ours.</strong> Our voice, our streets, our story.</span>
            </li>
          </ul>
        </Block>

        <Block eyebrow="A NOTE FROM ROBERT">
          <p className="italic border-l-4 border-[#DD5422] pl-6 py-2 text-[#B5BFB0]">
            I&apos;m 61 years old. I ain&apos;t chasing a record deal. I ain&apos;t trying to fit in with Nashville.
            I&apos;m tellin&apos; the truth about the people I know — the ones this country was built by and forgot about.
            If these songs mean something to you, then we&apos;re already family.
          </p>
          <p className="mt-6 font-anton uppercase tracking-widest text-sm text-[#DD5422]">
            Together we rise. — Robert
          </p>
        </Block>
      </section>

      {/* CTAs */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/music" data-testid="message-cta-music" className="group bg-[#151A14] border-2 border-[#DD5422] p-8 hover:bg-[#DD5422] transition-colors">
            <Music2 className="w-8 h-8 text-[#DD5422] group-hover:text-[#0A0E0B] mb-4" />
            <div className="font-anton text-2xl md:text-3xl uppercase tracking-tight leading-tight group-hover:text-[#0A0E0B]">
              Listen To The Album
            </div>
            <div className="text-sm text-[#8D9688] group-hover:text-[#0A0E0B]/80 mt-2 uppercase tracking-widest">
              8 tracks · Redneck Revolution
            </div>
            <div className="mt-6 flex items-center gap-2 text-[#DD5422] group-hover:text-[#0A0E0B] font-anton uppercase tracking-widest text-sm">
              Press play <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>

          <Link to="/shop" data-testid="message-cta-shop" className="group bg-[#151A14] border-2 border-[#2C332A] hover:border-[#DD5422] p-8 transition-colors">
            <ShoppingBag className="w-8 h-8 text-[#DD5422] mb-4" />
            <div className="font-anton text-2xl md:text-3xl uppercase tracking-tight leading-tight">
              Wear The Message
            </div>
            <div className="text-sm text-[#8D9688] mt-2 uppercase tracking-widest">
              Tees · hoodies · mugs · tanks
            </div>
            <div className="mt-6 flex items-center gap-2 text-[#DD5422] font-anton uppercase tracking-widest text-sm">
              Shop the merch <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </section>

      {/* Bottom heart */}
      <section className="border-t-2 border-[#2C332A] py-12 text-center">
        <div className="text-[#DD5422] font-anton uppercase tracking-[0.4em] text-xs flex items-center justify-center gap-2">
          <Heart className="w-4 h-4" fill="currentColor" /> Made with heart in the backroads <Heart className="w-4 h-4" fill="currentColor" />
        </div>
      </section>
    </div>
  );
}

function Block({ eyebrow, children }) {
  return (
    <div>
      <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-4">/ {eyebrow}</div>
      <div className="text-lg md:text-xl leading-relaxed text-[#B5BFB0]">{children}</div>
    </div>
  );
}
